import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMemo, useState } from "react";
import { endNotAfterStart, findOverlaps, type WindowDraft } from "./scheduleRules";

export interface SaveResult {
  ok: boolean;
  status?: number;
  version?: number;
  rejectedIndices?: Set<number>;
}

export interface WeeklyGridEditorProps {
  services: Array<{ id: string; nameFr: string }>;
  initialWindows: WindowDraft[];
  initialVersion: number;
  /** Injected save (parent wires react-query + the API); resolves with the CAS/validation outcome. */
  onSave: (version: number, windows: WindowDraft[]) => Promise<SaveResult>;
  /** Reload the authoritative schedule after a 409 conflict (parent refetches). */
  onReload: () => void;
}

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/**
 * The weekly hours editor (features/opening-hours-and-services.md): split shifts (many windows per
 * day), the cross-midnight confirm prompt (R2 — a window ending before it starts asks to confirm it
 * wraps past midnight, then saves flagged), the overlap highlight (R6 — offending windows are marked,
 * client-side AND from the server's typed rejection), and the 409 conflict dialog (§D.3 — a co-manager
 * edit offers reload-current). The server stays authoritative; this is fast local feedback.
 */
export function WeeklyGridEditor({
  services,
  initialWindows,
  initialVersion,
  onSave,
  onReload,
}: WeeklyGridEditorProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<WindowDraft[]>(initialWindows);
  const [version, setVersion] = useState(initialVersion);
  const [serverRejected, setServerRejected] = useState<Set<number>>(new Set());
  const [pendingCrossMidnight, setPendingCrossMidnight] = useState<number[] | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const clientOverlaps = useMemo(() => findOverlaps(rows), [rows]);
  const highlighted = useMemo(
    () => new Set<number>([...clientOverlaps, ...serverRejected]),
    [clientOverlaps, serverRejected],
  );

  const defaultServiceId = services[0]?.id ?? "";

  function addRow() {
    setRows((r) => [
      ...r,
      {
        dayOfWeek: 1,
        startTime: "19:00",
        endTime: "23:00",
        crossesMidnight: false,
        serviceId: defaultServiceId,
      },
    ]);
    setSaved(false);
  }

  function removeRow(index: number) {
    setRows((r) => r.filter((_, i) => i !== index));
    setServerRejected(new Set());
    setSaved(false);
  }

  function patchRow(index: number, patch: Partial<WindowDraft>) {
    setRows((r) => r.map((w, i) => (i === index ? { ...w, ...patch } : w)));
    setServerRejected(new Set());
    setSaved(false);
  }

  /** Indices whose end<=start but that are NOT yet flagged crossesMidnight — the R2 confirm set. */
  function unflaggedCrossMidnight(): number[] {
    return rows
      .map((w, i) => ({ w, i }))
      .filter(({ w }) => endNotAfterStart(w.startTime, w.endTime) && !w.crossesMidnight)
      .map(({ i }) => i);
  }

  async function persist(windows: WindowDraft[]) {
    setSaving(true);
    setConflict(false);
    setSaved(false);
    try {
      const result = await onSave(version, windows);
      if (result.ok) {
        setVersion(result.version ?? version);
        setServerRejected(new Set());
        setSaved(true);
      } else if (result.status === 409) {
        setConflict(true);
      } else {
        setServerRejected(result.rejectedIndices ?? new Set());
      }
    } finally {
      setSaving(false);
    }
  }

  function onSaveClick() {
    if (clientOverlaps.size > 0) return; // blocked; overlaps are highlighted
    const pending = unflaggedCrossMidnight();
    if (pending.length > 0) {
      setPendingCrossMidnight(pending);
      return;
    }
    void persist(rows);
  }

  function confirmCrossMidnight() {
    const flagged = rows.map((w, i) =>
      pendingCrossMidnight?.includes(i) ? { ...w, crossesMidnight: true } : w,
    );
    setRows(flagged);
    setPendingCrossMidnight(null);
    void persist(flagged);
  }

  function reloadAfterConflict() {
    setConflict(false);
    onReload();
  }

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "4" })}>
      <h3 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
        {t("schedule.weekly.title")}
      </h3>

      {conflict ? (
        <div
          role="alertdialog"
          aria-label={t("schedule.conflict.title")}
          className={css({ p: "3", borderWidth: "1px", rounded: "md", bg: "bg.subtle" })}
        >
          <p className={css({ fontSize: "sm" })}>{t("schedule.conflict.message")}</p>
          <button
            type="button"
            onClick={reloadAfterConflict}
            className={css({ mt: "2", px: "3", py: "1", borderWidth: "1px", rounded: "md" })}
          >
            {t("schedule.conflict.reload")}
          </button>
        </div>
      ) : null}

      {clientOverlaps.size > 0 ? (
        <output className={css({ display: "block", color: "fg.error", fontSize: "sm" })}>
          {t("schedule.weekly.overlapWarning")}
        </output>
      ) : null}

      <ul
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "2",
          listStyle: "none",
          p: "0",
        })}
      >
        {rows.map((w, index) => (
          <li
            key={`${index}-${w.serviceId}`}
            data-overlap={highlighted.has(index) ? "true" : undefined}
            className={css({
              display: "flex",
              gap: "2",
              alignItems: "center",
              flexWrap: "wrap",
              p: "2",
              borderWidth: "1px",
              rounded: "md",
              borderColor: highlighted.has(index) ? "border.error" : "border.default",
            })}
          >
            <label className={css({ srOnly: true })} htmlFor={`day-${index}`}>
              {t("schedule.weekly.day")}
            </label>
            <select
              id={`day-${index}`}
              value={w.dayOfWeek}
              onChange={(e) => patchRow(index, { dayOfWeek: Number(e.target.value) })}
              className={selectStyle}
            >
              {DAY_KEYS.map((d, i) => (
                <option key={d} value={i + 1}>
                  {t(`schedule.day.${d}`)}
                </option>
              ))}
            </select>
            <input
              type="time"
              aria-label={t("schedule.weekly.start")}
              value={w.startTime}
              onChange={(e) => patchRow(index, { startTime: e.target.value })}
              className={inputStyle}
            />
            <span aria-hidden>–</span>
            <input
              type="time"
              aria-label={t("schedule.weekly.end")}
              value={w.endTime}
              onChange={(e) => patchRow(index, { endTime: e.target.value })}
              className={inputStyle}
            />
            <select
              aria-label={t("schedule.weekly.service")}
              value={w.serviceId}
              onChange={(e) => patchRow(index, { serviceId: e.target.value })}
              className={selectStyle}
            >
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nameFr}
                </option>
              ))}
            </select>
            {w.crossesMidnight ? (
              <span className={css({ fontSize: "xs", color: "fg.muted" })}>
                {t("schedule.weekly.crossesMidnight")}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => removeRow(index)}
              className={css({ ml: "auto", px: "2", py: "1", fontSize: "sm" })}
            >
              {t("schedule.weekly.remove")}
            </button>
          </li>
        ))}
      </ul>

      <div className={css({ display: "flex", gap: "2" })}>
        <button
          type="button"
          onClick={addRow}
          className={css({ px: "3", py: "2", borderWidth: "1px", rounded: "md" })}
        >
          {t("schedule.weekly.addWindow")}
        </button>
        <button
          type="button"
          onClick={onSaveClick}
          disabled={saving || clientOverlaps.size > 0}
          className={css({
            px: "4",
            py: "2",
            rounded: "md",
            bg: "colorPalette.default",
            color: "colorPalette.fg",
            _disabled: { opacity: "0.5" },
          })}
        >
          {saving ? t("schedule.weekly.saving") : t("schedule.weekly.save")}
        </button>
      </div>

      {saved ? (
        <output className={css({ display: "block", color: "fg.muted", fontSize: "sm" })}>
          {t("schedule.weekly.saved")}
        </output>
      ) : null}

      {pendingCrossMidnight ? (
        <dialog
          open
          aria-label={t("schedule.crossMidnight.title")}
          className={css({
            position: "static",
            p: "3",
            borderWidth: "1px",
            rounded: "md",
            bg: "bg.subtle",
          })}
        >
          <p className={css({ fontSize: "sm" })}>{t("schedule.crossMidnight.message")}</p>
          <div className={css({ display: "flex", gap: "2", mt: "2" })}>
            <button
              type="button"
              onClick={confirmCrossMidnight}
              className={css({ px: "3", py: "1", borderWidth: "1px", rounded: "md" })}
            >
              {t("schedule.crossMidnight.confirm")}
            </button>
            <button
              type="button"
              onClick={() => setPendingCrossMidnight(null)}
              className={css({ px: "3", py: "1", borderWidth: "1px", rounded: "md" })}
            >
              {t("schedule.crossMidnight.cancel")}
            </button>
          </div>
        </dialog>
      ) : null}
    </section>
  );
}

const inputStyle = css({ px: "2", py: "1", borderWidth: "1px", rounded: "md" });
const selectStyle = css({ px: "2", py: "1", borderWidth: "1px", rounded: "md" });
